/**
 * Optimized sessionStorage wrapper with debouncing and batching
 * Reduces write operations to improve performance
 */

// Debounce timers for different storage keys
const debounceTimers = new Map();
const pendingWrites = new Map();

// Export for clearing pending writes when switching workflows
export const clearPendingWrite = (key) => {
  if (debounceTimers.has(key)) {
    clearTimeout(debounceTimers.get(key));
    debounceTimers.delete(key);
  }
  if (pendingWrites.has(key)) {
    pendingWrites.delete(key);
  }
};

// Default debounce delay (milliseconds)
const DEFAULT_DEBOUNCE_DELAY = 300;

/**
 * Debounced setItem - batches multiple writes to the same key
 * @param {string} key - Storage key
 * @param {*} value - Value to store (will be JSON.stringify'd if object)
 * @param {number} delay - Debounce delay in ms (default: 300ms)
 */
export const debouncedSetItem = (key, value, delay = DEFAULT_DEBOUNCE_DELAY) => {
  // Clear existing timer for this key
  if (debounceTimers.has(key)) {
    clearTimeout(debounceTimers.get(key));
  }

  // Store the pending value
  pendingWrites.set(key, value);

  // Set new timer
  const timer = setTimeout(() => {
    try {
      const finalValue = pendingWrites.get(key);
      if (finalValue !== undefined) {
        const serialized = typeof finalValue === 'string' 
          ? finalValue 
          : JSON.stringify(finalValue);
        sessionStorage.setItem(key, serialized);
      }
      pendingWrites.delete(key);
      debounceTimers.delete(key);
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn(`SessionStorage quota exceeded for key: ${key}`, error);
      } else {
        console.error(`Error writing to sessionStorage (key: ${key}):`, error);
      }
      pendingWrites.delete(key);
      debounceTimers.delete(key);
    }
  }, delay);

  debounceTimers.set(key, timer);
};

/**
 * Immediate setItem - writes immediately without debouncing
 * Use for critical writes that must happen right away
 * @param {string} key - Storage key
 * @param {*} value - Value to store
 */
export const immediateSetItem = (key, value) => {
  try {
    // Clear any pending debounced write for this key
    if (debounceTimers.has(key)) {
      clearTimeout(debounceTimers.get(key));
      debounceTimers.delete(key);
      pendingWrites.delete(key);
    }

    const serialized = typeof value === 'string' 
      ? value 
      : JSON.stringify(value);
    sessionStorage.setItem(key, serialized);
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn(`SessionStorage quota exceeded for key: ${key}`, error);
    } else {
      console.error(`Error writing to sessionStorage (key: ${key}):`, error);
    }
    throw error;
  }
};

/**
 * Batch setItem - writes multiple key-value pairs in a single operation
 * More efficient than multiple individual writes
 * @param {Object} keyValuePairs - Object with key-value pairs
 * @param {number} delay - Optional debounce delay (default: immediate)
 */
export const batchSetItems = (keyValuePairs, delay = 0) => {
  if (delay > 0) {
    // Debounce batch write
    Object.keys(keyValuePairs).forEach(key => {
      debouncedSetItem(key, keyValuePairs[key], delay);
    });
  } else {
    // Immediate batch write
    try {
      Object.keys(keyValuePairs).forEach(key => {
        // Clear any pending debounced write
        if (debounceTimers.has(key)) {
          clearTimeout(debounceTimers.get(key));
          debounceTimers.delete(key);
          pendingWrites.delete(key);
        }

        const value = keyValuePairs[key];
        const serialized = typeof value === 'string' 
          ? value 
          : JSON.stringify(value);
        sessionStorage.setItem(key, serialized);
      });
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('SessionStorage quota exceeded during batch write', error);
      } else {
        console.error('Error during batch write to sessionStorage:', error);
      }
      throw error;
    }
  }
};

/**
 * Get item from sessionStorage
 * @param {string} key - Storage key
 * @param {*} defaultValue - Default value if key doesn't exist
 * @returns {*} Parsed value or default
 */
export const getItem = (key, defaultValue = null) => {
  try {
    const item = sessionStorage.getItem(key);
    if (item === null) {
      return defaultValue;
    }

    // Check if it's a pending write (newer than storage)
    if (pendingWrites.has(key)) {
      return pendingWrites.get(key);
    }

    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(item);
    } catch {
      return item;
    }
  } catch (error) {
    console.error(`Error reading from sessionStorage (key: ${key}):`, error);
    return defaultValue;
  }
};

/**
 * Remove item from sessionStorage
 * Also clears any pending writes for this key
 * @param {string} key - Storage key
 */
export const removeItem = (key) => {
  try {
    // Clear pending write and timer
    if (debounceTimers.has(key)) {
      clearTimeout(debounceTimers.get(key));
      debounceTimers.delete(key);
      pendingWrites.delete(key);
    }

    sessionStorage.removeItem(key);
  } catch (error) {
    console.error(`Error removing from sessionStorage (key: ${key}):`, error);
  }
};

/**
 * Clear all pending writes (flush debounced operations)
 * Useful before unmount or critical operations
 */
export const flushPendingWrites = () => {
  debounceTimers.forEach((timer, key) => {
    clearTimeout(timer);
    try {
      const value = pendingWrites.get(key);
      if (value !== undefined) {
        const serialized = typeof value === 'string' 
          ? value 
          : JSON.stringify(value);
        sessionStorage.setItem(key, serialized);
      }
    } catch (error) {
      console.error(`Error flushing pending write (key: ${key}):`, error);
    }
    pendingWrites.delete(key);
  });
  debounceTimers.clear();
};

/**
 * Clear all items from sessionStorage
 * Also clears all pending writes and timers
 */
export const clear = () => {
  flushPendingWrites();
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
  }
};

